import React from 'react'
import {
  CloseIconSVG,
  MoreDownSVG,
  MoreUpSVG,
} from 'src/components/Icons/Basic'
import palette from 'src/designSystem/variables/palette'
import styled, { css } from 'styled-components/macro'

export type DockablePanelProps = {
  title: string
  // Small, muted text rendered immediately to the right of the title (not transformed).
  titleAdornment?: React.ReactNode
  description?: string
  closeable?: boolean
  attached?: boolean
  show?: boolean
  width?: number
  onAttach?: () => void
  onDetach?: () => void
  onClose?: () => void
  headerActions?: React.ReactNode
  children: React.ReactNode
  footer?: React.ReactNode
}

const DEFAULT_WIDTH = 280

const DockablePanelContainer = styled.div<{
  $isAttached?: boolean
  $width: number
}>`
  position: ${({ $isAttached }) => ($isAttached ? 'relative' : 'fixed')};
  height: 100%;
  top: 82px;
  right: 0;
  min-width: ${({ $width }) => $width}px;
  max-width: ${({ $width }) => $width}px;
  background-color: transparent;
  transition: position 0.3s ease-in-out, background-color 0.3s ease-in-out,
    box-shadow 0.3s ease-in-out, margin-left 0.3s ease-in-out;
  z-index: 1000;
  ${({ $isAttached }) =>
    $isAttached &&
    css`
      margin: 16px;
    `}
`

const DockablePanelContent = styled.div<{
  $isAttached?: boolean
  $width: number
}>`
  min-width: ${({ $width }) => $width}px;
  max-width: ${({ $width }) => $width}px;
  border-radius: 16px;
  border: 1px solid ${palette.neutral[100]};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 82px);
  max-height: calc(100vh - 82px);
  transition: position 0.3s ease-in-out, background-color 0.3s ease-in-out,
    box-shadow 0.3s ease-in-out, margin-left 0.3s ease-in-out;

  ${({ $isAttached }) =>
    $isAttached &&
    css`
      position: fixed;
      background-color: ${palette.neutral.white};
    `}

  ${({ $isAttached }) =>
    !$isAttached &&
    css`
      position: fixed;
      right: 16px;
      top: calc(16px + 50px); // 50px is the height of the header
      bottom: 16px;
      background-color: ${palette.neutral[30]};
      box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
    `}
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-self: flex-start;
  border-radius: 16px 16px 0 0;
  min-height: 55px;
  border-bottom: 1px solid ${palette.neutral[100]};
  padding: 16px;
  flex-shrink: 0;
  width: 100%;
`

const HeaderTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const TitleGroup = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
`

const Title = styled.span`
  font-size: 18px;
  font-weight: 500;
  color: ${palette.neutral[900]};
  text-transform: capitalize;
`

const TitleAdornment = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${palette.neutral[600]};
  white-space: nowrap;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  svg {
    width: 16px;
    height: 16px;
    cursor: pointer;
    path {
      fill: ${palette.neutral[800]};
    }
  }
`

const HeaderDescription = styled.span`
  margin-top: 4px;
  font-size: 12px;
  font-weight: 400;
  color: ${palette.neutral[600]};
`

const Content = styled.div<{ $hasFooter?: boolean }>`
  padding: 16px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  ${({ $hasFooter }) =>
    !$hasFooter &&
    css`
      border-radius: 0 0 16px 16px;
    `}
`

export const Footer = styled.div`
  padding: 16px;
  border-radius: 0 0 16px 16px;
  flex-shrink: 0;
  border-top: 1px solid ${palette.neutral[100]};
`

export const DockablePanel = ({
  title,
  titleAdornment,
  description,
  closeable = true,
  attached = false,
  show = false,
  width = DEFAULT_WIDTH,
  onDetach,
  onAttach,
  onClose,
  children,
  footer,
  headerActions,
}: DockablePanelProps): JSX.Element | null => {
  if (!show) {
    return null
  }

  return (
    <DockablePanelContainer $isAttached={attached} $width={width}>
      <DockablePanelContent $isAttached={attached} $width={width}>
        <Header>
          <HeaderTitleRow>
            <TitleGroup>
              <Title>{title.toLowerCase()}</Title>
              {titleAdornment && (
                <TitleAdornment>{titleAdornment}</TitleAdornment>
              )}
            </TitleGroup>
            <HeaderActions>
              {headerActions}
              <AttachControl
                isAttached={attached}
                onAttach={() => onAttach?.()}
                onDetach={() => onDetach?.()}
              />
              {closeable && <CloseIconSVG onClick={onClose} />}
            </HeaderActions>
          </HeaderTitleRow>
          {description && <HeaderDescription>{description}</HeaderDescription>}
        </Header>
        <Content $hasFooter={!!footer}>{children}</Content>
        {footer && <Footer>{footer}</Footer>}
      </DockablePanelContent>
    </DockablePanelContainer>
  )
}

type AttachControlProps = {
  isAttached: boolean
  onAttach?: () => void
  onDetach?: () => void
}

const AttachControl = ({
  isAttached,
  onAttach,
  onDetach,
}: AttachControlProps) => {
  const svg = isAttached ? <MoreDownSVG /> : <MoreUpSVG />

  // TODO fake disable for now
  if (svg) {
    return <></>
  }

  return (
    <div
      onClick={() => (isAttached ? onDetach?.() : onAttach?.())}
      style={{ cursor: 'pointer' }}
    >
      {svg}
    </div>
  )
}
